import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, Cross1Icon } from '@radix-ui/react-icons';
import Select, { GroupBase, StylesConfig } from 'react-select';

import { TPlantWithAccessControl } from './user-plant-access-management.tsx';
import { SelectStyles } from '@/features/plants/components/plant-management/shared/select-styles.ts';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';

const PLANTS_PER_PAGE = 5;

export const UserPlantAccessManagementMobile = ({
  plants,
  setPlants,
  isLoading,
}: {
  plants: TPlantWithAccessControl[];
  setPlants: React.Dispatch<React.SetStateAction<TPlantWithAccessControl[]>>;
  isLoading: boolean;
}) => {
  const availablePlants = plants
    ?.filter((p) => p.columnId === 'available-plants')
    ?.map((p) => ({
      value: p.plantId,
      label: `${p.basicDetails.name}`,
    }));
  const associatedPlants = plants
    .filter((p) => p.columnId === 'associated-plants')
    .sort((a, b) => {
      if (a.newlyAdded && !b.newlyAdded) return -1;
      if (!a.newlyAdded && b.newlyAdded) return 1;
      return 0;
    });

  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLastPlant = currentPage * PLANTS_PER_PAGE;
  const indexOfFirstPlant = indexOfLastPlant - PLANTS_PER_PAGE;
  const currentPlants = associatedPlants.slice(indexOfFirstPlant, indexOfLastPlant);
  const numOfPages = associatedPlants?.length
    ? Math.trunc((associatedPlants.length - 1) / PLANTS_PER_PAGE) + 1
    : 1;

  const selectValue: { label: string; value: string } = {
    label: availablePlants?.length ? 'Assign a plant' : 'All available plants are assigned',
    value: 'select',
  };

  if (isLoading)
    return (
      <div className="flex min-h-[332px] w-full flex-col">
        <Skeleton className="h-9" />

        <div className="mt-4 flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
        <div className="mt-4 flex h-full items-center justify-between">
          <Skeleton className="h-5 w-16" />

          <div className="flex gap-2">
            <Skeleton className="size-8" />
            <Skeleton className="size-8" />
          </div>
        </div>
      </div>
    );

  return (
    <div className="mt-4 flex min-h-[332px] flex-col justify-between">
      <div>
        <Select
          value={selectValue}
          isDisabled={!availablePlants?.length}
          isSearchable={false}
          options={availablePlants}
          placeholder="Assign a plant"
          onChange={(selectedOption) => {
            const option = selectedOption as {
              value: string;
              label: string;
            } | null;

            if (option) {
              const plantId = option.value;

              if (plantId) {
                setPlants((current) =>
                  current?.map((p) => {
                    if (p.plantId === plantId)
                      return {
                        ...p,
                        columnId: 'associated-plants',
                        newlyAdded: true,
                      };

                    return p;
                  })
                );
              }
            }
          }}
          styles={{
            ...(SelectStyles as StylesConfig<
              { label: string; value: string },
              boolean,
              GroupBase<{ label: string; value: string }>
            >),
            menu: (styles) => ({
              ...styles,
              ...SelectStyles.menu,
              maxHeight: '200px',
              overflowY: 'scroll',
            }),
          }}
        />

        <ScrollArea>
          <ul className="mt-4 flex max-h-[260px] flex-col gap-2">
            {currentPlants.map((plant) => {
              return (
                <li
                  key={plant.plantId}
                  className="flex justify-between rounded-lg bg-slate-100 px-4 py-2 aria-disabled:opacity-50 aria-disabled:hover:ring-0"
                >
                  <div>
                    <span className="inline-block">{plant.basicDetails.name}</span>{' '}
                    {plant.newlyAdded && (
                      <span className="inline-block size-2 rounded-full bg-blue-400"></span>
                    )}
                  </div>
                  <div></div>
                  <button
                    onClick={() => {
                      setPlants((current) =>
                        current?.map((p) => {
                          if (p.plantId === plant.plantId)
                            return {
                              ...p,
                              columnId: 'available-plants',
                            };

                          return p;
                        })
                      );

                      if (currentPlants.length % 5 === 1) {
                        setCurrentPage((currentPage) =>
                          currentPage === 1 ? currentPage : currentPage - 1
                        );
                      }
                    }}
                    className="hover:ring-primary p-1 hover:ring-2 hover:ring-inset"
                  >
                    <Cross1Icon />
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
            Page {currentPage} of {numOfPages}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (currentPage <= 1) return;
                setCurrentPage((current) => current - 1);
              }}
              disabled={currentPage <= 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (currentPage >= numOfPages) return;
                setCurrentPage((current) => current + 1);
              }}
              disabled={currentPage >= numOfPages}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
